import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Inbox, ChevronLeft, Check, X, MessageSquare, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_COLOUR = {
  pending: 'bg-blue-100 text-blue-800',
  viewed: 'bg-purple-100 text-purple-800',
  accepted: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
  expired: 'bg-gray-200 text-gray-700',
};

export default function CompanyInvitations() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState(null); // accept | decline | info
  const [reason, setReason] = useState('');
  const [question, setQuestion] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/api/company-invitations/received`, {
      credentials: 'include', headers: authHeaders(),
    });
    if (r.ok) setList((await r.json()).invitations || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const callAction = async (path, body) => {
    const r = await fetch(`${API}${path}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body || {}),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      toast.error(typeof err.detail === 'string' ? err.detail : (err.detail?.message || 'Action failed'));
      return false;
    }
    return true;
  };

  const handleAccept = async () => {
    const ok = await callAction(`/api/company-invitations/${active.invitation_id}/accept`, { reason });
    if (ok) {
      toast.success(`Building linked: ${active.building_name}`);
      setActive(null); setMode(null); setReason('');
      fetchList();
    }
  };
  const handleDecline = async () => {
    const ok = await callAction(`/api/company-invitations/${active.invitation_id}/decline`, { reason });
    if (ok) {
      toast.success('Invitation declined');
      setActive(null); setMode(null); setReason('');
      fetchList();
    }
  };
  const handleInfo = async () => {
    if (!question.trim()) return toast.error('Please write a question');
    const ok = await callAction(`/api/company-invitations/${active.invitation_id}/request-info`, { question });
    if (ok) {
      toast.success('Question sent to building admin');
      setActive(null); setMode(null); setQuestion('');
    }
  };

  const open = async (inv) => {
    setActive(inv);
    // Auto-mark viewed
    if (inv.status === 'pending') {
      await fetch(`${API}/api/company-invitations/${inv.invitation_id}/mark-viewed`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
      });
      fetchList();
    }
  };

  if (!['platform_owner', 'super_agent', 'agent_admin'].includes(user?.role)) {
    return <div className="p-8 text-center text-gray-500">Management Company access required.</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" data-testid="company-invitations-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-gray-200">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <Inbox className="w-7 h-7 text-indigo-600" /> Building Invitations
            </h1>
            <p className="text-gray-600 text-sm">Buildings inviting your company to manage them.</p>
          </div>
        </div>

        {loading ? (
          <Card><CardContent className="p-8 text-center text-gray-500">Loading…</CardContent></Card>
        ) : list.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center">
              <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No building invitations yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {list.map((inv) => (
              <Card
                key={inv.invitation_id}
                className="cursor-pointer hover:shadow-md transition"
                onClick={() => open(inv)}
                data-testid={`inv-row-${inv.invitation_id}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 bg-indigo-50 rounded-lg shrink-0">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{inv.building_name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      Invited by {inv.invited_by_email} · {new Date(inv.created_at).toLocaleString()}
                    </div>
                    {inv.message && (
                      <div className="text-sm text-gray-700 mt-1 line-clamp-1">"{inv.message}"</div>
                    )}
                  </div>
                  <Badge variant="outline" className={`capitalize ${STATUS_COLOUR[inv.status] || ''}`}>
                    {inv.status}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Invitation detail */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setMode(null); } }}>
        <DialogContent className="max-w-lg" data-testid="invitation-detail">
          <DialogHeader>
            <DialogTitle>{active?.building_name}</DialogTitle>
            <DialogDescription>
              Invitation from <strong>{active?.invited_by_email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div><span className="text-gray-500">Status:</span> <Badge variant="outline" className={`capitalize ${STATUS_COLOUR[active?.status] || ''}`}>{active?.status}</Badge></div>
            <div><span className="text-gray-500">Sent:</span> {active && new Date(active.created_at).toLocaleString()}</div>
            <div><span className="text-gray-500">Expires:</span> {active && new Date(active.expires_at).toLocaleString()}</div>
            {active?.is_replacement && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-amber-800 text-xs">
                ⚠️ This is a replacement — accepting will end the current management assignment.
              </div>
            )}
            {active?.message && (
              <div className="p-3 bg-gray-50 rounded">{active.message}</div>
            )}

            {mode === 'accept' && (
              <div><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional welcome note" data-testid="accept-note" /></div>
            )}
            {mode === 'decline' && (
              <div><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for declining" data-testid="decline-reason" /></div>
            )}
            {mode === 'info' && (
              <div><Textarea rows={4} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="What additional info do you need?" data-testid="info-question" /></div>
            )}
          </div>
          <DialogFooter className="flex flex-wrap gap-2">
            {mode === null && active?.status !== 'accepted' && active?.status !== 'declined' && active?.status !== 'expired' && (
              <>
                <Button variant="outline" onClick={() => setMode('info')} data-testid="btn-info"><MessageSquare className="w-4 h-4 mr-1" /> Request Info</Button>
                <Button variant="outline" onClick={() => setMode('decline')} data-testid="btn-decline"><X className="w-4 h-4 mr-1 text-red-600" /> Decline</Button>
                <Button onClick={() => setMode('accept')} data-testid="btn-accept"><Check className="w-4 h-4 mr-1" /> Accept</Button>
              </>
            )}
            {mode === 'accept' && <Button onClick={handleAccept} className="bg-green-600 hover:bg-green-700" data-testid="confirm-accept">Confirm Accept</Button>}
            {mode === 'decline' && <Button onClick={handleDecline} className="bg-red-600 hover:bg-red-700" data-testid="confirm-decline">Confirm Decline</Button>}
            {mode === 'info' && <Button onClick={handleInfo} data-testid="confirm-info">Send Question</Button>}
            {mode && <Button variant="outline" onClick={() => setMode(null)}>Back</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
